// api.js — the live backend client. Talks to the Goose control-plane server
// (Express, :3001) which serves RLS-scoped data out of Supabase. Every request
// sends the session cookie (credentials:'include'); auth is cookie-backed so the
// browser carries it automatically once /auth/login succeeds.
//
// This module is the ONLY place the frontend knows a URL. Screens never fetch —
// they read window.MAV, which lib/hydrate.js populates from these calls.
//
// Exposed as window.GooseAPI (the app's window.* dialect) and as ES exports.

import { buildStartRequest, listBuildStartRequests, forgetBuildStartRequest, recordBuildStartResult } from './build-start-request.js';
import { retainEspIntent, listEspIntents, recordEspIntent, retireEspIntent } from './esp-conversion-request.js';

function buildContext() {
  const m = window.MAV, actor = m?.currentUser?.id, workspace = m?.activeWorkspaceId;
  if (!actor || !workspace || m.contextLoad?.status !== 'ready' || m.viewerContext !== workspace) {
    throw Error('Choose a ready single workspace before starting or recovering a build.');
  }
  const contextKey = m.__contextKey, epoch = m.__contextEpoch;
  return { key: JSON.stringify([actor, workspace]), headers: { 'X-Goose-Start-Context': JSON.stringify([actor, workspace]) },
    check() {
      const now = window.MAV;
      if (now.__contextKey !== contextKey || now.__contextEpoch !== epoch || now.currentUser?.id !== actor
        || now.activeWorkspaceId !== workspace || now.viewerContext !== workspace || now.contextLoad?.status !== 'ready') {
        throw Error('Your account or workspace changed. Reload before retrying this build.');
      }
    } };
}

// Empty string = relative paths, which nginx proxies to the api container.
// Set VITE_API_BASE in .env for local dev pointing directly at :3001.
const BASE = (import.meta.env && import.meta.env.VITE_API_BASE) || '';

// Default per-request timeout (ISS-17). Tunable via VITE_API_TIMEOUT_MS at build
// time, or window.__GOOSE_REQ_TIMEOUT_MS at runtime (tests set a short value).
const REQ_TIMEOUT_MS = (import.meta.env && Number(import.meta.env.VITE_API_TIMEOUT_MS)) || 15000;

async function espRequest(job, suffix, options = {}) {
  const context = buildContext(); context.check();
  const result = await req('/api/builds/' + encodeURIComponent(job) + '/esp/' + suffix, { ...options, headers: { ...options.headers, ...context.headers } });
  context.check(); return result;
}
async function afterburnerRead(job, suffix) {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(job || '')) throw Error('Invalid Afterburner build identity.');
  const context = buildContext(); context.check();
  const result = await req('/api/builds/' + encodeURIComponent(job) + '/afterburner/operations' + suffix,
    { headers: context.headers });
  context.check(); return result;
}
async function espOperationRequest(job, id, execute = false) {
  const context = buildContext(), result = await espRequest(job, 'operations/' + encodeURIComponent(id) + (execute ? '/execute' : ''),
    execute ? { method: 'POST', body: {}, timeoutMs: 120000 } : {});
  context.check();
  try {
    for (const row of listEspIntents(context.key, job)) if (row.operation_id === id) await recordEspIntent(context.key, job, row.key, result);
  } catch {
    // Database history must remain readable even if browser storage was revoked.
    // New admissions still require durable browser intent before their POST.
    result.browser_recovery_warning = 'Browser recovery history could not be updated. The server operation remains authoritative.';
  }
  context.check(); return result;
}

async function req(path, { method = 'GET', body, timeoutMs, headers = {} } = {}) {
  const opts = {
    method,
    credentials: 'include',
    headers: { ...headers },
  };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  // Bound every request (ISS-17): a hung connection must NEVER spin the UI
  // forever. On timeout the AbortController fires → a typed {kind:'timeout'}
  // error (same family as {kind:'network'}), so callers (login.jsx, the app.jsx
  // me-probe) surface a real error instead of an endless "Signing in…".
  const ms = timeoutMs || (typeof window !== 'undefined' && Number(window.__GOOSE_REQ_TIMEOUT_MS) > 0
    ? Number(window.__GOOSE_REQ_TIMEOUT_MS) : REQ_TIMEOUT_MS);
  const ctl = new AbortController();
  opts.signal = ctl.signal;
  const timer = setTimeout(() => ctl.abort(), ms);
  let res, text;
  try {
    res = await fetch(BASE + path, opts);
    text = await res.text();
  } catch (err) {
    if (err && err.name === 'AbortError') {
      const e = new Error('timeout'); e.kind = 'timeout'; e.cause = err; throw e;
    }
    // Server unreachable (down, CORS, offline). Typed so callers can degrade
    // gracefully (e.g. fall back to mock data) vs. a real auth fail.
    const e = new Error('network'); e.kind = 'network'; e.cause = err; throw e;
  } finally {
    clearTimeout(timer);
  }
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!res.ok) {
    const e = new Error((data && data.error) || `HTTP ${res.status}`);
    e.kind = res.status === 401 ? 'auth' : 'http';
    e.status = res.status;
    e.data = data;
    throw e;
  }
  return data;
}

// Build a query string from an object, dropping null/''/'all' (the "no filter"
// sentinel the issues directory uses). Returns '' or '?a=1&b=2'.
function qs(obj) {
  if (!obj) return '';
  const p = Object.entries(obj).filter(([, v]) => v != null && v !== '' && v !== 'all')
    .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v));
  return p.length ? '?' + p.join('&') : '';
}

const GooseAPI = {
  base: BASE,
  // ── auth ──
  me: () => req('/api/auth/me'),
  login: (email, password) => req('/api/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => req('/api/auth/logout', { method: 'POST' }),
  register: (payload) => req('/api/auth/register', { method: 'POST', body: payload }),
  // AUTH-02: password lifecycle (self-service change, admin-mediated forgot, sign-out-everywhere)
  changePassword: (current_password, new_password) => req('/api/auth/password', { method: 'POST', body: { current_password, new_password } }),
  forgotPassword: (email) => req('/api/auth/forgot', { method: 'POST', body: { email } }),
  logoutAll: () => req('/api/auth/logout-all', { method: 'POST' }),
  // RBAC-07: switch the session's active workspace (re-derive the viewer from the
  // response) + the audited admin/PM org-wide build list (org breadth comes ONLY
  // from here — never a relaxed tenant query).
  switchWorkspace: (workspaceId) => req('/api/auth/switch-workspace', { method: 'POST', body: { workspaceId } }),
  orgBuilds: (limit) => req('/api/org/builds' + (limit ? '?limit=' + encodeURIComponent(limit) : '')),
  // ── data (RLS-scoped to the session's workspace) ──
  builds: (workspaceId) => req('/api/data/builds' + qs({ workspace_id: workspaceId })),
  build: (id) => req('/api/data/builds/' + encodeURIComponent(id)),
  summary: () => req('/api/data/summary'),
  costByStage: () => req('/api/data/cost-by-stage'),
  metrics: () => req('/api/data/metrics'),
  clients: () => req('/api/data/clients'),
  client: (slug) => req('/api/data/clients/' + encodeURIComponent(slug)),
  // ── KB edit proposals (dev submit → independent review → immutable publication) ──
  kbProposals: () => req('/api/kb/proposals'),
  kbProposal: (key) => req('/api/kb/proposals/' + encodeURIComponent(key)),
  kbSubmit: (payload) => req('/api/kb/proposals', { method: 'POST', body: payload }),
  kbDecide: (key, payload) => req('/api/kb/proposals/' + encodeURIComponent(key) + '/decision', { method: 'POST', body: payload }),
  // ── Admin cost controls (per-workspace swarm cost ceiling; ISS-25) ──
  getCostControls: () => req('/api/admin/cost-controls'),
  saveCostControls: (cfg) => req('/api/admin/cost-controls', { method: 'PUT', body: cfg }),
  // PY-44: persist a workspace's default build engine (admin/PM; active-workspace-scoped).
  setWorkspaceEngine: (workspaceId, defaultEngine) =>
    req('/api/admin/workspace-engine', { method: 'PUT', body: { workspaceId, defaultEngine } }),
  // ── New Build pre-flight ──────────────────────────────
  tierCosts: () => req('/api/data/tier-costs'),
  openrouterCredits: () => req('/api/data/openrouter-credits'),
  codexRateLimit: () => req('/api/data/codex-rate-limit'),
  eoaStatus: () => req('/api/data/eoa-status'),
  // ── Dropbox delivery (DBX-02) ──
  dropboxDeliver: (jobId) => req('/api/dropbox/deliver', { method: 'POST', body: { jobId } }),
  // ── build trigger ─────────────────────────────────────
  startBuild: async (payload) => {
    if (payload.dry_run) return req('/api/builds/start', { method: 'POST', body: payload });
    const context = buildContext();
    const request = await buildStartRequest(payload, context.key);
    context.check();
    if (request.retained) {
      // Recover a known submission without repeating mutable upload/profile or
      // vendor-token preflight. This GET has no launch side effects.
      const prior = await req('/api/builds/start-requests/' + encodeURIComponent(request.key), { headers: context.headers });
      context.check(); request.record(prior);
      if (prior.found) return prior;
    }
    context.check();
    const result = await req('/api/builds/start', { method: 'POST', body: payload,
      headers: { ...context.headers, 'Idempotency-Key': request.key } });
    context.check(); request.record(result);
    // Keep completed receipts too. Only explicit new-build intent retires them;
    // a late concurrent caller or reload must not create another paid request.
    return result;
  },
  savedBuildRequests: () => listBuildStartRequests(buildContext().key),
  checkBuildRequest: async key => {
    const context = buildContext();
    const result = await req('/api/builds/start-requests/' + encodeURIComponent(key), { headers: context.headers });
    context.check(); recordBuildStartResult(context.key, key, result); return result;
  },
  newBuildIntent: key => forgetBuildStartRequest(buildContext().key, key),
  deleteBuild: (jobId) => req('/api/builds/' + encodeURIComponent(jobId), { method: 'DELETE' }),
  renderBuild: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/render', { method: 'POST' }),
  assignBuildClient: (jobId, clientSlug) =>
    req('/api/builds/' + encodeURIComponent(jobId) + '/client', {
      method: 'POST',
      body: { client_slug: clientSlug || null },
    }),
  /*
   * Reserved editor seams (RW-03) — a method is ADDED here by the ticket that lands its route; the workspace panes
   * treat presence as wired (RW-01c/d) and show the ticket-named copy while a seam is absent. Landed so far:
   * editorSession (RWB-2 #91), listRevisions / readRevision / restoreRevision (RWB-3 #90), listAssets / replaceAsset
   * (RWB-5 #92), listComments / addComment / replyComment / resolveComment + notifications (RWB-8 #95), and RWB-4 #112:
   *   proposeAmendment(jobId, body) / applyAmendment(jobId, id, body) / rejectAmendment(jobId, id, body)
   *   listAmendments(jobId) / flywheelUnits(jobId) / certifyRevision(jobId, r, body) / readRevisionQa(jobId, r).
   * RWB-7: chasePropose queues an operation; chaseProposals / chaseOperation read server state;
   * chaseDiscard / chaseCancel end pending work; chaseAccept replays, renders, and runs QA in one call.
   * Nothing executable is reserved — no stubs, no fake results.
   */

  reviewBuild: (jobId, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/review', { method: 'POST', body }),
  // RWB-2: the read-only editor session over the exact active revision (EditorProjection.v1)
  editorSession: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/editor-session'),
  // RWB-8: comments bound to (job, revision_digest, region_id|null) + in-app notifications
  listComments: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/comments'),
  addComment: (jobId, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/comments', { method: 'POST', body }),           // {region_id|null, revision_digest, body, mentions[]}
  replyComment: (jobId, id, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/comments/' + encodeURIComponent(id) + '/replies', { method: 'POST', body }),   // {body, mentions[]}
  resolveComment: (jobId, id, open) => req('/api/builds/' + encodeURIComponent(jobId) + '/comments/' + encodeURIComponent(id), { method: 'PATCH', body: { open } }),
  listNotifications: (opts = {}) => req('/api/notifications' + (opts.unread ? '?unread=1' : '')),
  markNotificationRead: (id) => req('/api/notifications/' + encodeURIComponent(id) + '/read', { method: 'POST' }),
  markAllNotificationsRead: () => req('/api/notifications/read-all', { method: 'POST' }),
  // RWB-3: the artifact revision graph (History → Revisions; approve sends revision_digest)
  listRevisions: (jobId, { cursor, limit } = {}) => {
    const query = new URLSearchParams();
    if (cursor != null) query.set('cursor', cursor);
    if (limit != null) query.set('limit', limit);
    return req('/api/builds/' + encodeURIComponent(jobId) + '/revisions' + (query.size ? '?' + query : ''));
  },
  readRevision: (jobId, r) => req('/api/builds/' + encodeURIComponent(jobId) + '/revisions/' + encodeURIComponent(r)),
  readRevisionAssets: (jobId, r) => req('/api/builds/' + encodeURIComponent(jobId) + '/revisions/' + encodeURIComponent(r) + '?assets=1'),
  restoreRevision: (jobId, r, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/revisions/' + encodeURIComponent(r) + '/restore', { method: 'POST', body }),
  // RWB-4: amendment replay and pending-head certification (server owns all gates).
  proposeAmendment: (jobId, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/amendments/propose', { method: 'POST', body, timeoutMs: 600000 }),
  applyAmendment: (jobId, id, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/amendments/' + encodeURIComponent(id) + '/apply', { method: 'POST', body, timeoutMs: 600000 }),
  rejectAmendment: (jobId, id, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/amendments/' + encodeURIComponent(id) + '/reject', { method: 'POST', body }),
  listAmendments: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/amendments'),
  flywheelUnits: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/flywheel/units'),
  certifyRevision: (jobId, r, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/revisions/' + encodeURIComponent(r) + '/certify', { method: 'POST', body, timeoutMs: 600000 }),
  readRevisionQa: (jobId, r) => req('/api/builds/' + encodeURIComponent(jobId) + '/revisions/' + encodeURIComponent(r) + '/qa'),
  qualificationReview: async (job, body = null, key = null) => {
    const context=buildContext();context.check();
    const result=await req('/api/builds/'+encodeURIComponent(job)+'/qualification-review',body===null
      ? {headers:context.headers}
      : {method:'POST',body,headers:{...context.headers,'Idempotency-Key':key},timeoutMs:30000});
    context.check();return result;
  },
  currentQualificationReview: async (job, body = null, key = null) => {
    const context=buildContext();context.check();
    const result=await req('/api/builds/'+encodeURIComponent(job)+'/qualification-review/current',body===null
      ? {headers:context.headers}
      : {method:'POST',body,headers:{...context.headers,'Idempotency-Key':key},timeoutMs:30000});
    context.check();return result;
  },
  historicalQualificationReview: async (job, digest) => {
    const context=buildContext();context.check();
    const result=await req('/api/builds/'+encodeURIComponent(job)+'/qualification-review/current/'+encodeURIComponent(digest),{headers:context.headers});
    context.check();return result;
  },
  readEspChecks: (jobId, r) => req('/api/builds/' + encodeURIComponent(jobId) + '/revisions/' + encodeURIComponent(r) + '/esp-checks'),
  espCapabilities: job => espRequest(job, 'capabilities'),
  espHistory: (job, before = null) => espRequest(job, 'operations' + (before ? '/history/' + encodeURIComponent(before) : '')),
  espOperation: (job, id) => espOperationRequest(job, id),
  espPair: (job, id) => espRequest(job, 'operations/' + encodeURIComponent(id) + '/pair'),
  espInspectionHistory: (job, id, before = null) => espRequest(job, 'operations/' + encodeURIComponent(id) + '/inspections' + (before ? '/history/' + encodeURIComponent(before) : '')),
  espInspection: (job, id, digest) => espRequest(job, 'operations/' + encodeURIComponent(id) + '/inspections/' + encodeURIComponent(digest)),
  espBoard: (job, id) => espRequest(job, 'operations/' + encodeURIComponent(id) + '/board'),
  executeEspConversion: (job, id) => espOperationRequest(job, id, true),
  savedEspRequests: job => listEspIntents(buildContext().key, job),
  recoverEspRequest: async (job, key) => {
    const context = buildContext(), result = await espRequest(job, 'requests/' + encodeURIComponent(key));
    context.check();
    try { await recordEspIntent(context.key, job, key, result); }
    catch { result.browser_recovery_warning = 'Browser recovery history could not be updated. The server operation remains authoritative.'; }
    context.check(); return result;
  },
  admitEspConversion: async (job, body) => {
    const context = buildContext(), intent = await retainEspIntent(context.key, job, body);
    context.check();
    const result = await espRequest(job, 'operations', { method: 'POST', body: intent.body, headers: { 'Idempotency-Key': intent.key }, timeoutMs: 120000 });
    context.check();
    try { await recordEspIntent(context.key, job, intent.key, result); }
    catch { result.browser_recovery_warning = 'Browser recovery history could not be updated. Recover the original saved key before creating another request.'; }
    context.check(); return result;
  },
  newEspIntent: (job, key) => retireEspIntent(buildContext().key, job, key),
  readEspPolicy: (jobId, esp, espTier = null) => req('/api/builds/' + encodeURIComponent(jobId) + '/esp/policy?esp=' + encodeURIComponent(esp) + (espTier === null ? '' : '&esp_tier=' + encodeURIComponent(espTier))),
  proposeEspPolicy: (jobId, payload) => req('/api/builds/' + encodeURIComponent(jobId) + '/esp/policy-proposals', { method: 'POST', body: payload }),
  espDefaultContext: (jobId,revision,digest) => espRequest(jobId,'default-context?revision='+encodeURIComponent(revision)+'&revision_digest='+encodeURIComponent(digest)),
  proposeEspDefault: (jobId,payload) => espRequest(jobId,'default-proposals',{method:'POST',body:payload}),
  runEspCheck: (jobId, r, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/revisions/' + encodeURIComponent(r) + '/esp-checks', { method: 'POST', body, timeoutMs: 30000 }),

  chasePropose: (jobId, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/chase/propose', { method: 'POST', body }),
  chaseProposals: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/chase/proposals'),
  chaseOperation: (jobId, op) => req('/api/builds/' + encodeURIComponent(jobId) + '/chase/operations/' + encodeURIComponent(op)),
  chaseDiscard: (jobId, cp) => req('/api/builds/' + encodeURIComponent(jobId) + '/chase/proposals/' + encodeURIComponent(cp) + '/discard', { method: 'POST' }),
  chaseCancel: (jobId, op) => req('/api/builds/' + encodeURIComponent(jobId) + '/chase/operations/' + encodeURIComponent(op) + '/cancel', { method: 'POST' }),
  chaseAccept: (jobId, cp, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/chase/proposals/' + encodeURIComponent(cp) + '/accept', { method: 'POST', body, timeoutMs: 600000 }),
  chaseAcceptAll: (jobId, body) => req('/api/builds/' + encodeURIComponent(jobId) + '/chase/accept-all', { method: 'POST', body, timeoutMs: 600000 }),
  // RWB-5: hardened asset edit on the active revision (multipart — raw fetch keeps the boundary)
  listAssets: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/assets'),
  replaceAsset: async (jobId, { file, mode = 'replace', name, newName, blockId, expected_head, expected_head_digest, reason }) => {
    const fd = new FormData();
    fd.append('mode', mode); if (name) fd.append('name', name); if (blockId) fd.append('block_id', blockId);
    if(newName)fd.append('new_name',newName);
    fd.append('expected_head', expected_head); fd.append('expected_head_digest', expected_head_digest);
    if (reason) fd.append('reason', reason);
    if (!['remove','rename'].includes(mode)) fd.append('file', file, name || (file && file.name));
    const res = await fetch(BASE + '/api/builds/' + encodeURIComponent(jobId) + '/assets', { method: 'POST', credentials: 'include', body: fd });
    let data = null; try { data = await res.json(); } catch { data = null; }
    if (!res.ok) { const e = new Error((data && data.error) || `HTTP ${res.status}`); e.status = res.status; e.data = data; throw e; }
    return data;
  },
  cancelBuild: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/cancel', { method: 'POST' }),
  retryBuild: async (jobId, expectedAttemptId) => {
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(jobId) || !/^[a-f0-9]{32}$/.test(expectedAttemptId || '')) {
      throw Error('Load the signed original attempt before retrying. Older unsigned builds need a new upload.');
    }
    const context = buildContext(), scope = JSON.stringify(['retry', context.key, jobId]);
    const body = { expected_attempt_id: expectedAttemptId };
    const receipt = await buildStartRequest(body, scope); context.check();
    if (receipt.retained) {
      const prior = await req('/api/builds/retry-requests/' + encodeURIComponent(receipt.key), { headers: context.headers });
      context.check(); receipt.record(prior); if (prior.found) return prior;
    }
    context.check();
    const result = await req('/api/builds/' + encodeURIComponent(jobId) + '/retry', { method: 'POST', body,
      headers: { ...context.headers, 'Idempotency-Key': receipt.key } });
    context.check(); receipt.record(result); return result;
  },
  savedRetryRequests: jobId => listBuildStartRequests(JSON.stringify(['retry', buildContext().key, jobId])),
  checkRetryRequest: async (jobId, key) => {
    const context = buildContext(), scope = JSON.stringify(['retry', context.key, jobId]);
    if (!listBuildStartRequests(scope).some(r => r.key === key)) throw Error('Retry receipt does not belong to this build and workspace.');
    const result = await req('/api/builds/retry-requests/' + encodeURIComponent(key), { headers: context.headers });
    context.check(); recordBuildStartResult(scope, key, result); return result;
  },
  buildAttempts: jobId => req('/api/builds/' + encodeURIComponent(jobId) + '/attempts'),
  reconcileBuild: async (jobId, expectedAttemptId) => {
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(jobId) || !/^[a-f0-9]{32}$/.test(expectedAttemptId || '')) {
      throw Error('Reload the signed build before reconciling worker status.');
    }
    const context=buildContext();
    const result=await req('/api/builds/'+encodeURIComponent(jobId)+'/reconcile', {method:'POST',
      body:{expected_attempt_id:expectedAttemptId},headers:context.headers});
    context.check();return result;
  },
  afterburnerStatus: (jobId) => req('/api/builds/' + encodeURIComponent(jobId) + '/afterburner'),
  afterburnerHistory: (jobId, cursor = null) => {
    if (cursor !== null && (typeof cursor !== 'string' || !/^[A-Za-z0-9_-]{1,4096}$/.test(cursor))) {
      throw Error('Invalid Afterburner history cursor. Refresh retained checks.');
    }
    return afterburnerRead(jobId, '?limit=20' + (cursor ? '&cursor=' + encodeURIComponent(cursor) : ''));
  },
  afterburnerReview: (jobId, operationId) => {
    if (!/^[a-f0-9]{64}$/.test(operationId || '')) throw Error('Invalid retained Afterburner operation.');
    return afterburnerRead(jobId, '/' + operationId + '/review');
  },
  startAfterburner: (jobId, force = false, intake = {}) =>
    req('/api/builds/' + encodeURIComponent(jobId) + '/afterburner', {
      method: 'POST',
      body: { force, method: intake.method || 'api', url: intake.url || null },
    }),
  // multipart upload — bypasses the JSON req() helper because FormData
  // mustn't have its Content-Type stamped (the browser sets the boundary).
  uploadBrief: async (formData) => {
    // Generous bound (uploads are large) but still finite — never hang forever (ISS-17).
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 120000);
    let res, text;
    try {
      res = await fetch(BASE + '/api/builds/upload', { method: 'POST', credentials: 'include', body: formData, signal: ctl.signal });
      text = await res.text();
    } catch (err) {
      if (err && err.name === 'AbortError') { const e = new Error('timeout'); e.kind = 'timeout'; e.cause = err; throw e; }
      const e = new Error('network'); e.kind = 'network'; e.cause = err; throw e;
    } finally { clearTimeout(timer); }
    let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
    if (!res.ok) { const e = new Error((data && data.error) || `HTTP ${res.status}`); e.status = res.status; e.data = data; throw e; }
    return data;
  },
  // ── Sabre developer-gold profile onboarding ──────────
  sabreProfiles: () => req('/api/sabre/profiles'),
  sabreProfileApprovalQueue: () => req('/api/sabre/profiles/proposals'),
  sabreProfileProposal: (requestId) =>
    req('/api/sabre/profiles/proposals/' + encodeURIComponent(requestId)),
  cancelSabreProfileProposal: (requestId) =>
    req('/api/sabre/profiles/proposals/' + encodeURIComponent(requestId) + '/cancel', {
      method: 'POST', body: {},
    }),
  amendSabreProfile: (requestId, draft) =>
    req('/api/sabre/profiles/proposals/' + encodeURIComponent(requestId) + '/draft', {
      method: 'PUT', body: draft,
    }),
  submitSabreProfile: (requestId) =>
    req('/api/sabre/profiles/proposals/' + encodeURIComponent(requestId) + '/submit', {
      method: 'POST', body: {},
    }),
  reviewSabreProfile: (requestId, action) =>
    req('/api/sabre/profiles/proposals/' + encodeURIComponent(requestId) + '/review', {
      method: 'POST', body: { action },
    }),
  proposeSabreProfile: async (formData) => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 120000);
    let res, text;
    try {
      res = await fetch(BASE + '/api/sabre/profiles/proposals', {
        method: 'POST', credentials: 'include', body: formData, signal: ctl.signal,
      });
      text = await res.text();
    } catch (err) {
      if (err && err.name === 'AbortError') { const e = new Error('timeout'); e.kind = 'timeout'; e.cause = err; throw e; }
      const e = new Error('network'); e.kind = 'network'; e.cause = err; throw e;
    } finally { clearTimeout(timer); }
    let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
    if (!res.ok) { const e = new Error((data && data.error) || `HTTP ${res.status}`); e.status = res.status; e.data = data; throw e; }
    if (!data || !data.request_id) return data;
    const deadline = Date.now() + 20 * 60 * 1000;
    let operation = data;
    while (operation.operation_status === 'queued' || operation.operation_status === 'running') {
      if (Date.now() >= deadline) {
        const error = new Error('Profile analysis is still running. Reopen it from the review queue.');
        error.kind = 'timeout'; error.data = operation; throw error;
      }
      const delay = Math.max(250, Math.min(Number(operation.retry_after_ms) || 1000, 5000));
      await new Promise((resolvePromise) => setTimeout(resolvePromise, delay));
      operation = await req('/api/sabre/profiles/proposals/' + encodeURIComponent(data.request_id), {
        timeoutMs: 30000,
      });
    }
    if (operation.operation_status !== 'succeeded') {
      const error = new Error((operation.error && operation.error.message) || 'Profile analysis did not complete.');
      error.kind = 'profile_operation'; error.data = operation; throw error;
    }
    return operation;
  },
  // ORG-04: recorded uploads + SME approval. uploadFiles mirrors uploadBrief's raw-fetch
  // multipart pattern (FormData must keep its own boundary); the rest ride req().
  uploadFiles: async (formData) => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 120000);
    let res, text;
    try {
      res = await fetch(BASE + '/api/uploads', { method: 'POST', credentials: 'include', body: formData, signal: ctl.signal });
      text = await res.text();
    } catch (err) {
      if (err && err.name === 'AbortError') { const e = new Error('timeout'); e.kind = 'timeout'; e.cause = err; throw e; }
      const e = new Error('network'); e.kind = 'network'; e.cause = err; throw e;
    } finally { clearTimeout(timer); }
    let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
    if (!res.ok) { const e = new Error((data && data.error) || `HTTP ${res.status}`); e.status = res.status; e.data = data; throw e; }
    return data;
  },
  uploadsPending: () => req('/api/uploads/pending'),
  uploadGet: (id) => req('/api/uploads/' + encodeURIComponent(id)),
  uploadDecide: (id, body) => req('/api/uploads/' + encodeURIComponent(id) + '/decision', { method: 'POST', body }),
  // Build the artifact URL (used by <img src> + the viewer modal)
  artifactUrl: (jobId, relPath) =>
    BASE + '/api/events/' + encodeURIComponent(jobId) + '/artifact?path=' + encodeURIComponent(relPath),
  // Delivery package (the swarm's 05_approval/<name>.zip). Resolved server-side
  // because the zip isn't always recorded as an artifact event.
  deliveryUrl: (jobId) =>
    BASE + '/api/events/' + encodeURIComponent(jobId) + '/delivery',
  fetchArtifactText: async (jobId, relPath) => {
    const res = await fetch(BASE + '/api/events/' + encodeURIComponent(jobId) + '/artifact?path=' + encodeURIComponent(relPath), {
      credentials: 'include',
    });
    if (!res.ok) { const e = new Error('HTTP ' + res.status); e.status = res.status; throw e; }
    const ct = res.headers.get('content-type') || '';
    const text = await res.text();
    return { text, contentType: ct };
  },
  team: () => req('/api/data/team'),
  // ── issues + ratings (Jira-lite, /api/issues) ──
  issues: (params) => req('/api/issues' + qs(params)),
  issue: (key) => req('/api/issues/' + encodeURIComponent(key)),
  createIssue: (body) => req('/api/issues', { method: 'POST', body }),
  patchIssue: (key, body) => req('/api/issues/' + encodeURIComponent(key), { method: 'PATCH', body }),
  commentIssue: (key, text) => req('/api/issues/' + encodeURIComponent(key) + '/comment', { method: 'POST', body: { text } }),
  submitFeedback: (body) => req('/api/issues/feedback', { method: 'POST', body }),
  ratingsSummary: () => req('/api/issues/ratings/summary'),
};

if (typeof window !== 'undefined') window.GooseAPI = GooseAPI;
export default GooseAPI;
